import type { Conversation, IdentificationStatus } from "@/types";

/** Committed or draft filter state: empty array = no constraint ("All") for that dimension. */
export interface ChatAppliedFilters {
  status: string[];
  intention: string[];
  customerID: string[];
  channel: string[];
}

/** Workflow-only status labels (maps to API `status` + `statusToLabel`). */
export const ALL_STATUS_LABELS = [
  "Needs Review",
  "Escalated",
  "AI Handled",
  "Resolved",
  "Needs Follow-up",
] as const;

/** Full STATUS section in filter panel (Unread first). */
export const ALL_STATUS_PANEL_VALUES = [
  "Unread",
  ...ALL_STATUS_LABELS,
] as const;

export const ALL_INTENT_VALUES = ["Booking", "Rebook", "Cancel", "Info", "Schedule", "Account"] as const;

export const ALL_CUSTOMER_IDS = ["identified", "id_pending", "unmatched"] as const;

export const ALL_CHANNELS = ["instagram", "whatsapp", "facebook", "email"] as const;

export const EMPTY_CHAT_FILTERS: ChatAppliedFilters = {
  status: [],
  intention: [],
  customerID: [],
  channel: [],
};

export function cloneChatFilters(f: ChatAppliedFilters): ChatAppliedFilters {
  return {
    status: [...f.status],
    intention: [...f.intention],
    customerID: [...f.customerID],
    channel: [...f.channel],
  };
}

/** If none or all options are selected, treat as "All" (no filter). */
export function normalizeChatFilter<T extends string>(selected: string[], allValues: readonly T[]): T[] {
  const allowed = new Set(allValues as readonly string[]);
  const picked = selected.filter((s) => allowed.has(s)) as T[];
  if (picked.length === 0) return [];
  if (picked.length === allValues.length && allValues.every((v) => picked.includes(v))) return [];
  return picked;
}

export function normalizedChatFilters(f: ChatAppliedFilters): ChatAppliedFilters {
  return {
    status: normalizeChatFilter(f.status, ALL_STATUS_PANEL_VALUES),
    intention: normalizeChatFilter(f.intention, ALL_INTENT_VALUES),
    customerID: normalizeChatFilter(f.customerID, ALL_CUSTOMER_IDS),
    channel: normalizeChatFilter(f.channel, ALL_CHANNELS),
  };
}

export function activeFilterCount(f: ChatAppliedFilters): number {
  const n = normalizedChatFilters(f);
  return n.status.length + n.intention.length + n.customerID.length + n.channel.length;
}

/** Same mapping as conversation list badges / filter (needs_followup → "Needs Follow-up"). */
export function conversationStatusToPanelLabel(status: string): string {
  if (status === "needs_followup") return "Needs Follow-up";
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Client-side chat list filtering (must stay in sync with GET /conversations narrowing in Chats). */
export function conversationMatchesChatFilters(
  c: Conversation,
  appliedFilters: ChatAppliedFilters,
  search: string
): boolean {
  if (search && !c.customerName.toLowerCase().includes(search.toLowerCase())) return false;
  const norm = normalizedChatFilters(appliedFilters);
  const nsPanel = normalizeChatFilter(norm.status, ALL_STATUS_PANEL_VALUES);
  if (nsPanel.length > 0) {
    const ok = nsPanel.some((s) => {
      if (s === "Unread") return c.unread;
      return conversationStatusToPanelLabel(c.status) === s;
    });
    if (!ok) return false;
  }
  const ni = normalizeChatFilter(norm.intention, ALL_INTENT_VALUES);
  if (ni.length > 0 && !(ni as string[]).includes(c.intent)) return false;
  const nc = normalizeChatFilter(norm.channel, ALL_CHANNELS);
  if (nc.length > 0 && !(nc as string[]).includes(c.channel)) return false;
  const nid = normalizeChatFilter(norm.customerID, ALL_CUSTOMER_IDS);
  if (nid.length > 0 && !(nid as string[]).includes(c.identificationStatus)) return false;
  return true;
}

export function filterConversationsForChatList(
  conversations: Conversation[],
  appliedFilters: ChatAppliedFilters,
  search: string
): Conversation[] {
  return conversations.filter((c) => conversationMatchesChatFilters(c, appliedFilters, search));
}

export const INTENT_VALUE_TO_LABEL: Record<string, string> = {
  Booking: "New Booking",
  Rebook: "Rebooking",
  Cancel: "Cancellation",
  Info: "General Info",
  Schedule: "Schedule",
  Account: "Membership",
};

export const CUSTOMER_ID_TO_LABEL: Record<string, string> = {
  identified: "Identified",
  id_pending: "ID Pending",
  unmatched: "Unmatched",
};

export const CHANNEL_VALUE_TO_LABEL: Record<string, string> = {
  instagram: "Instagram",
  whatsapp: "WhatsApp",
  email: "Email",
};

export function statusLabelToTagClass(label: string): string {
  switch (label) {
    case "Unread":
      return "bg-primary/15 text-primary border-primary/25";
    case "Needs Review":
      return "bg-warning/15 text-warning border-warning/25";
    case "Escalated":
      return "bg-destructive/15 text-destructive border-destructive/25";
    case "AI Handled":
      return "bg-success/15 text-success border-success/25";
    case "Resolved":
      return "bg-success/15 text-success border-success/25";
    case "Needs Follow-up":
      return "bg-info/15 text-info border-info/25";
    default:
      return "bg-muted text-muted-foreground border-border/60";
  }
}

export function intentionValueToTagClass(value: string): string {
  switch (value) {
    case "Booking":
      return "bg-success/15 text-success border-success/25";
    case "Rebook":
      return "bg-info/15 text-info border-info/25";
    case "Cancel":
      return "bg-warning/15 text-warning border-warning/25";
    case "Info":
      return "bg-primary/15 text-primary border-primary/25";
    case "Schedule":
      return "bg-[hsl(180_50%_40%)]/15 text-[hsl(180_50%_35%)] border-[hsl(180_50%_40%)]/25";
    case "Account":
      return "bg-secondary/15 text-secondary border-secondary/25";
    default:
      return "bg-muted text-muted-foreground border-border/60";
  }
}

export function customerIdToTagClass(id: IdentificationStatus | string): string {
  switch (id) {
    case "identified":
      return "bg-success/15 text-success border-success/25";
    case "id_pending":
      return "bg-warning/15 text-warning border-warning/25";
    case "unmatched":
      return "bg-destructive/15 text-destructive border-destructive/25";
    default:
      return "bg-muted text-muted-foreground border-border/60";
  }
}

export function channelValueToTagClass(_value: string): string {
  return "bg-muted/60 text-foreground border-border/60";
}

export type ChatFilterType = "status" | "intention" | "customerID" | "channel";
